import React from 'react';
import { t } from '@lingui/macro';
import {
    Button,
    ButtonGroup,
    GenericSkeleton,
    ListUnorderedOutlined,
    useBreakpoint,
} from '@landr/maestro';
import PlayerButton from 'components/CommonUi/PlayerButton/PlayerButton';
import { RouterLink } from 'components/CommonUi/RouterLink';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { noop } from 'utils/noop';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { SlideTypes } from '../types';
import { getViewCtaLink } from '../helpers/getViewCtaLink';
import { getViewCtaCopy } from '../helpers/getViewCtaCopy';
import { useHeroCarouselContext } from '../context/HeroCarouselContext';

const useCallToAction = ({
    type,
    slug,
    disableAutoPlay,
    enableAutoPlay,
}: {
    type?: SlideTypes;
    slug?: string;
    disableAutoPlay: () => void;
    enableAutoPlay: () => void;
}): {
    viewCtaLink: string;
    viewCtaCopy: string;
    playCtaCopy: string;
    handlePlayDemoClicked: (playing: boolean) => void;
    handleViewClicked: () => void;
} => {
    const isSmallMobile = useBreakpoint('sm');
    const toLocalizedPath = useToLocalizedPath();

    const viewCtaLink = getViewCtaLink(toLocalizedPath, type, slug);
    const viewCtaCopy = isSmallMobile ? t`View` : getViewCtaCopy(type);
    const playCtaCopy = isSmallMobile ? t`Demo` : t`Play demo`;

    const handlePlayDemoClicked = (playing: boolean) => {
        playing ? disableAutoPlay() : enableAutoPlay();
    };

    const handleViewClicked = () => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewDetail,
            ctaType: CtaType.PlainText,
            interactionSource: InteractionSource.Banner,
            ctaDestination: viewCtaLink,
            ctaText: viewCtaCopy,
        });
    };

    return {
        viewCtaLink,
        viewCtaCopy,
        playCtaCopy,
        handlePlayDemoClicked,
        handleViewClicked,
    };
};

const CallToActions = ({
    isLoading,
    isVertical,
    type,
    slug,
    demoGuid,
    playDemoCallback = noop,
}: {
    isLoading: boolean;
    isVertical: boolean;
    type?: SlideTypes;
    slug?: string;
    demoGuid?: string;
    playDemoCallback?: (totalTime: number) => void;
}) => {
    const { disableAutoPlay, enableAutoPlay } = useHeroCarouselContext();
    const {
        viewCtaLink,
        viewCtaCopy,
        playCtaCopy,
        handlePlayDemoClicked,
        handleViewClicked,
    } = useCallToAction({
        type,
        slug,
        disableAutoPlay,
        enableAutoPlay,
    });

    if (isLoading || !type || !slug || !demoGuid) {
        return (
            <ButtonGroup mt={isVertical ? 'sm' : 'xxl'}>
                <GenericSkeleton
                    width="136px"
                    height="48px"
                    mb={4}
                    variant="round"
                />
                <GenericSkeleton
                    width="136px"
                    height="48px"
                    mb={4}
                    variant="round"
                />
            </ButtonGroup>
        );
    }

    return (
        <ButtonGroup mt={isVertical ? 'sm' : 'xxl'}>
            <PlayerButton
                key="PlayerButton"
                id={demoGuid}
                variant="secondary"
                isRounded
                onClick={handlePlayDemoClicked}
                callback={playDemoCallback}
            >
                {playCtaCopy}
            </PlayerButton>
            <Button
                as={RouterLink}
                to={viewCtaLink}
                isRounded
                variant="primary"
                kind="solid"
                icon={ListUnorderedOutlined}
                onClick={handleViewClicked}
            >
                {viewCtaCopy}
            </Button>
        </ButtonGroup>
    );
};

export default CallToActions;
